import React, { useState, useCallback, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { GoogleMap, Marker, useJsApiLoader, Autocomplete, Libraries } from '@react-google-maps/api';
import FormField from '@/components/common/FormField';
import Button from '@/components/common/Button';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { showToastNotification } from '@/utility/helper';
import { errorTypes } from '@/utility/validation';
import { ICreateDriverForm, DriverDetailResponse } from '@/interfaces/Driver/driver.interface';
import { Layout } from '@/components/Layout/Layout';
import { MainHeading } from '@/components/common/MainHeading';
import { SectionCard } from '@/components/common/SectionCard';
import { MapPin, User, Car, Landmark, FileText, CheckCircle2 } from 'lucide-react';
import MultiImageUploader from '@/components/common/MultiImageUploader';
import { CommonSelect } from '@/components/common/CommonSelect';
import { MultiSelect, MultiSelectOption } from '@/components/common/MultiSelect';
import { COUNTRY_CODES } from '@/utility/Country';
import { useActiveRole } from '@/hooks/useActiveRole';

const GOOGLE_API_KEY = import.meta.env.VITE_APP_GOOGLE_API_KEY || '';

const defaultLocation = {
    lat: -26.204103, // Default to Johannesburg, South Africa
    lng: 28.047305
};

const mapContainerStyle = {
    width: '100%',
    height: '400px',
    borderRadius: '12px'
};

const LIBRARIES: Libraries = ['places'];

export const AddDriver: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const isEditMode = !!id;
    const navigate = useNavigate();
    const { prefix } = useActiveRole();
    const [removeMedia, setRemoveMedia] = useState<string[]>([]);
    const [mediaMap, setMediaMap] = useState<Record<string, string>>({});
    const [loadingData, setLoadingData] = useState(isEditMode);
    const [markerPosition, setMarkerPosition] = useState(defaultLocation);
    const [searchBox, setSearchBox] = useState<google.maps.places.Autocomplete | null>(null);

    const [zoneOptions, setZoneOptions] = useState<MultiSelectOption[]>([]);
    const [zonesLoading, setZonesLoading] = useState(false);

    const {
        register,
        handleSubmit,
        setValue,
        reset,
        control,
        formState: { errors, isSubmitting },
    } = useForm<ICreateDriverForm>({
        defaultValues: {
            countryCode: '+27',
            addressType: 'home',
            accountType: 'saving',
            latitude: defaultLocation.lat,
            longitude: defaultLocation.lng,
        }
    });
    const fetchZones = useCallback(async (search: string = '') => {
        setZonesLoading(true);
        try {
            const response = await callApi<any>(
                `${API_URL}${API_ENDPOINT.zones}?limit=10${search ? `&search=${search}` : ''}`,
                { method: 'GET' }
            );
            const zonesData = response?.data?.zones || response?.data || [];
            const mappedOptions = zonesData.map((z: any) => ({
                label: z.name,
                value: z.id,
            }));
            setZoneOptions(mappedOptions);
        } catch (error) {
            console.error("Failed to fetch zones", error);
        } finally {
            setZonesLoading(false);
        }
    }, []);


    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_API_KEY,
        libraries: LIBRARIES
    });

    useEffect(() => {
        fetchZones();
        const fetchDriver = async () => {
            if (!id) return;
            try {
                const response = await callApi<DriverDetailResponse>(
                    `${API_URL}${API_ENDPOINT.user}/detail/${id}`,
                    { method: 'GET' }
                );
                const driver = response?.data;
                const address = driver.addresses?.[0];
                const lat = address?.latitude ? parseFloat(address.latitude.toString()) : defaultLocation.lat;
                const lng = address?.longitude ? parseFloat(address.longitude.toString()) : defaultLocation.lng;

                // Store media mapping
                const mapping: Record<string, string> = {};
                driver.media?.forEach((m: any) => {
                    const url = `${API_URL}/${m.url}`;
                    mapping[url] = m.id;
                });
                setMediaMap(mapping);

                setMarkerPosition({ lat, lng });

                reset({
                    fullName: driver.fullName || '',
                    email: driver.email || '',
                    countryCode: driver.countryCode || '+27',
                    phone: driver.phone || '',
                    vehicleType: driver.driverInfo?.vehicleType || '',
                    vehicleNumber: driver.driverInfo?.vehicleNumber || '',
                    addressType: address?.addressType || 'home',
                    addressLine: address?.addressLine || '',
                    latitude: lat,
                    longitude: lng,
                    bankName: driver.wallet?.bankName || '',
                    branchName: driver.wallet?.branchName || '',
                    branchCode: driver.wallet?.branchCode || '',
                    accountType: driver.wallet?.accountType || 'saving',
                    accountNumber: driver.wallet?.accountNumber || '',
                    drivingLicense: driver.media?.filter(m => m.referenceType === 'driving_license').map(m => `${API_URL}/${m.url}`) || [],
                    southAfricaId: driver.media?.filter(m => m.referenceType === 'south_africa_id').map(m => `${API_URL}/${m.url}`) || [],
                    vehicleImage: driver.media?.filter(m => m.referenceType === 'vehicle_image').map(m => `${API_URL}/${m.url}`) || [],
                    profilePhoto: driver.media?.filter(m => m.referenceType === 'profile_picture').map(m => `${API_URL}/${m.url}`) || [],
                    zones: driver.driverZones?.map((dz: any) => dz.zoneId || dz.zone?.id || dz.id) || [],
                });
            } catch (error) {
                ThrowError(error);
                navigate(-1);
            } finally {
                setLoadingData(false);
            }
        };

        if (isEditMode) {
            fetchDriver();
        } else {
            setValue('latitude', defaultLocation.lat, { shouldValidate: true });
            setValue('longitude', defaultLocation.lng, { shouldValidate: true });
        }
    }, [id, isEditMode, reset, setValue, navigate]);

    const onMarkerDragEnd = useCallback((e: google.maps.MapMouseEvent) => {
        if (e.latLng) {
            const newLat = e.latLng.lat();
            const newLng = e.latLng.lng();
            setMarkerPosition({ lat: newLat, lng: newLng });
            setValue('latitude', newLat, { shouldValidate: true });
            setValue('longitude', newLng, { shouldValidate: true });

            // Reverse geocoding to auto-fill addressLine
            if (window.google && window.google.maps && window.google.maps.Geocoder) {
                const geocoder = new google.maps.Geocoder();
                geocoder.geocode({ location: { lat: newLat, lng: newLng } }, (results, status) => {
                    if (status === 'OK' && results && results[0]) {
                        setValue('addressLine', results[0].formatted_address, { shouldValidate: true });
                    }
                });
            }
        }
    }, [setValue]);

    const onLoadAutocomplete = (autocomplete: google.maps.places.Autocomplete) => {
        setSearchBox(autocomplete);
    };

    const onPlaceChanged = () => {
        if (searchBox !== null) {
            const place = searchBox.getPlace();
            if (place.geometry && place.geometry.location) {
                const newLat = place.geometry.location.lat();
                const newLng = place.geometry.location.lng();
                setMarkerPosition({ lat: newLat, lng: newLng });
                setValue('latitude', newLat, { shouldValidate: true });
                setValue('longitude', newLng, { shouldValidate: true });

                if (place.formatted_address) {
                    setValue('addressLine', place.formatted_address, { shouldValidate: true });
                }
            }
        }
    };

    const handleMediaChange = (field: any, newValue: (File | string)[]) => {
        if (isEditMode) {
            const removedImages = (field.value || []).filter((item: any) =>
                typeof item === 'string' && !newValue.includes(item)
            ) as string[];

            if (removedImages.length > 0) {
                const newDeletedIds = removedImages
                    .map(url => mediaMap[url])
                    .filter(id => id && !removeMedia.includes(id));

                if (newDeletedIds.length > 0) {
                    setRemoveMedia(prev => [...prev, ...newDeletedIds]);
                }
            }
        }
        field.onChange(newValue);
    };

    const onSubmit = async (data: ICreateDriverForm) => {
        try {
            const url = isEditMode
                ? `${API_URL}${API_ENDPOINT.updateDriverProfile}/${id}`
                : `${API_URL}${API_ENDPOINT.createDriver}`;

            const method = isEditMode ? 'PUT' : 'POST';

            const formData = new FormData();

            // 1. Personal & Credential Fields
            formData.append('fullName', data.fullName?.trim() || '');
            formData.append('email', data.email?.trim() || '');
            formData.append('countryCode', data.countryCode || '+27');
            formData.append('phone', data.phone?.trim() || '');

            if (!isEditMode) {
                formData.append('password', data.password || '');
            } else if (data.password) {
                formData.append('password', data.password);
            }

            // 2. Vehicle Details
            formData.append('vehicleType', data.vehicleType?.trim() || '');
            formData.append('vehicleNumber', data.vehicleNumber?.trim() || '');

            // 3. Address Fields
            formData.append('addressType', data.addressType?.trim() || '');
            formData.append('addressLine', data.addressLine?.trim() || '');
            formData.append('latitude', markerPosition.lat.toString());
            formData.append('longitude', markerPosition.lng.toString());

            // 4. Bank Fields
            formData.append('bankName', data.bankName?.trim() || '');
            formData.append('branchName', data.branchName?.trim() || '');
            formData.append('branchCode', data.branchCode?.trim() || '');
            formData.append('accountType', data.accountType?.trim() || '');
            formData.append('accountNumber', data.accountNumber?.trim() || '');

            // 5. Binary Media Files
            if (data.drivingLicense && data.drivingLicense.length > 0) {
                data.drivingLicense.forEach((file) => {
                    if (typeof file !== 'string') formData.append('drivingLicense', file);
                });
            }
            if (data.southAfricaId && data.southAfricaId.length > 0) {
                data.southAfricaId.forEach((file) => {
                    if (typeof file !== 'string') formData.append('southAfricaId', file);
                });
            }
            if (data.vehicleImage && data.vehicleImage.length > 0) {
                data.vehicleImage.forEach((file) => {
                    if (typeof file !== 'string') formData.append('vehicleImage', file);
                });
            }
            if (data.profilePhoto && data.profilePhoto.length > 0) {
                data.profilePhoto.forEach((file) => {
                    if (typeof file !== 'string') formData.append('profilePhoto', file);
                });
            }

            if (isEditMode && removeMedia.length > 0) {
                formData.append('deleteMedia', JSON.stringify(removeMedia));
            }

            if (data.zones && data.zones.length > 0) {
                formData.append('zoneIds', JSON.stringify(data.zones));
            }

            const response = await callApi(
                url,
                {
                    method,
                    data: formData,
                    multipart: true
                }
            );

            showToastNotification('success', response?.message || (isEditMode ? 'Driver account updated successfully.' : 'Driver account created successfully.'));
            navigate(prefix + '/drivers');
        } catch (error) {
            ThrowError(error);
        }
    };

    if (loadingData) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[60vh]">
                    <div className="animate-spin h-10 w-10 border-4 border-primary border-t-transparent rounded-full" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6 pb-20 animate-in fade-in duration-500">
                <MainHeading
                    title={isEditMode ? "Edit Driver Account" : "Register Driver Account"}
                    description={isEditMode ? "Update driver credentials, vehicle details, physical address, and bank information." : "Fill out credentials, vehicle details, physical address, and bank information to create a driver account."}
                    showBack
                    onBack={() => navigate(-1)}
                />

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    {/* ── 1. Credentials & Personal Details ── */}
                    <SectionCard title="Credentials & Personal Information" icon={<User className="w-5 h-5 text-muted-foreground" />}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormField
                                label="Full Name"
                                requiredIcon
                                placeholder="Enter driver's full name"
                                error={errors.fullName?.message}
                                {...register('fullName', { ...errorTypes.required('Full Name') })}
                            />
                            <FormField
                                label="Email Address"
                                type="email"
                                requiredIcon
                                placeholder="driver@example.com"
                                error={errors.email?.message}
                                {...register('email', {
                                    ...errorTypes.required('Email'),
                                    ...errorTypes.email,
                                })}
                            />
                            <div className="grid grid-cols-4 gap-4">
                                <div className="col-span-1">
                                    <Controller
                                        name="countryCode"
                                        control={control}
                                        render={({ field }) => (
                                            <div className="flex flex-col">
                                                <label className="block text-sm font-medium text-foreground mb-1.5">Code</label>
                                                <CommonSelect
                                                    value={field.value}
                                                    onValueChange={field.onChange}
                                                    options={COUNTRY_CODES}
                                                    error={errors.countryCode?.message}
                                                    triggerClassName="w-full h-10 rounded-md border-border shadow-none"
                                                    disabled
                                                />
                                            </div>
                                        )}
                                    />
                                </div>
                                <div className="col-span-3">
                                    <FormField
                                        label="Phone Number"
                                        requiredIcon
                                        placeholder="123456789"
                                        wrapperClass="m-0"
                                        error={errors.phone?.message}
                                        onInput={(e: React.FormEvent<HTMLInputElement>) => {
                                            const target = e.target as HTMLInputElement;
                                            target.value = target.value.replace(/[^0-9]/g, '');
                                        }}
                                        {...register('phone', {
                                            ...errorTypes.required('Phone Number'),
                                            ...errorTypes.phone,
                                        })}
                                    />
                                </div>
                            </div>
                            {!isEditMode && (
                                <FormField
                                    label="Password"
                                    type="password"
                                    requiredIcon
                                    placeholder="Min. 6 characters"
                                    error={errors.password?.message}
                                    {...register('password', {
                                        required: 'Password is required',
                                        minLength: { value: 6, message: 'Password must be at least 6 characters' },
                                    })}
                                />
                            )}
                        </div>
                    </SectionCard>

                    {/* ── 2. Operational Zones ── */}
                    <SectionCard title="Operational Zones" icon={<MapPin className="w-5 h-5 text-muted-foreground" />}>
                        <Controller
                            name="zones"
                            control={control}
                            rules={{ required: 'At least one zone must be assigned' }}
                            render={({ field }) => (
                                <div className="flex flex-col">
                                    <label className="block text-sm font-medium text-foreground mb-1.5">
                                        Assigned Zones
                                        <span className="text-destructive ml-1">*</span>
                                    </label>
                                    <MultiSelect
                                        options={zoneOptions}
                                        selected={field.value || []}
                                        onChange={field.onChange}
                                        onSearchChange={(value) => {
                                            setTimeout(() => {
                                                fetchZones(value)
                                            }, 1000);

                                        }}
                                        placeholder="Search and select operating zones..."
                                        loading={zonesLoading}
                                    />
                                    {errors.zones && <p className="text-xs text-destructive mt-1">{errors.zones.message}</p>}
                                </div>
                            )}
                        />
                    </SectionCard>

                    {/* ── 3. Vehicle Details ── */}
                    <SectionCard title="Vehicle Specification" icon={<Car className="w-5 h-5 text-muted-foreground" />}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormField
                                label="Vehicle Type"
                                type="text"
                                requiredIcon
                                placeholder="e.g. Sedan, Bike, Truck"
                                error={errors.vehicleType?.message}
                                {...register('vehicleType', { required: 'Vehicle Type is required' })}
                            />
                            <FormField
                                label="Vehicle Number / Plate"
                                requiredIcon
                                placeholder="e.g. AB 12 CD GP"
                                error={errors.vehicleNumber?.message}
                                {...register('vehicleNumber', { required: 'Vehicle Number is required' })}
                            />
                        </div>
                    </SectionCard>

                    {/* ── 4. Spatial Data & Physical Address ── */}
                    <SectionCard title="Physical Address & Spatial Data" icon={<MapPin className="w-5 h-5 text-muted-foreground" />}>
                        <p className="text-xs text-muted-foreground mb-4">
                            Search for an address or move the map marker to set geographic coordinates automatically.
                        </p>

                        {isLoaded && (
                            <div className="mb-4">
                                <Autocomplete
                                    onLoad={onLoadAutocomplete}
                                    onPlaceChanged={onPlaceChanged}
                                >
                                    <input
                                        type="text"
                                        placeholder="Search for a location or address..."
                                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                    />
                                </Autocomplete>
                            </div>
                        )}

                        <div className="overflow-hidden border border-secondary/20 rounded-xl bg-secondary/5 mb-6">
                            {isLoaded ? (
                                <GoogleMap
                                    mapContainerStyle={mapContainerStyle}
                                    center={markerPosition}
                                    zoom={15}
                                    options={{ zoomControl: true, streetViewControl: false, mapTypeControl: false }}
                                >
                                    <Marker
                                        position={markerPosition}
                                        draggable={true}
                                        onDragEnd={onMarkerDragEnd}
                                        animation={google.maps.Animation.DROP}
                                    />
                                </GoogleMap>
                            ) : (
                                <div className="flex items-center justify-center w-full h-[400px] bg-secondary/10">
                                    <span className="text-sm text-muted-foreground animate-pulse">Loading Google Maps...</span>
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <FormField
                                label="Address Type"
                                type="select"
                                requiredIcon
                                options={[
                                    { label: 'Home', value: 'home' },
                                    { label: 'Work', value: 'work' },
                                    { label: 'Other', value: 'other' },
                                ]}
                                error={errors.addressType?.message}
                                {...register('addressType', { required: 'Address Type is required' })}
                            />
                        </div>

                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormField
                                label="Full Address Line"
                                type="textarea"
                                rows={2}
                                requiredIcon
                                placeholder="Complete street address block"
                                error={errors.addressLine?.message}
                                {...register('addressLine', { required: 'Address Line is required' })}
                            />
                        </div>
                    </SectionCard>

                    {/* ── 4. Banking Information ── */}
                    <SectionCard title="Banking Details" icon={<Landmark className="w-5 h-5 text-muted-foreground" />}>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <FormField
                                label="Bank Name"
                                requiredIcon
                                placeholder="e.g. Standard Bank"
                                error={errors.bankName?.message}
                                {...register('bankName', { required: 'Bank Name is required' })}
                            />
                            <FormField
                                label="Branch Name"
                                requiredIcon
                                placeholder="e.g. Sandton Branch"
                                error={errors.branchName?.message}
                                {...register('branchName', { required: 'Branch Name is required' })}
                            />
                            <FormField
                                label="Branch Code"
                                requiredIcon
                                placeholder="e.g. 051001"
                                error={errors.branchCode?.message}
                                {...register('branchCode', { required: 'Branch Code is required' })}
                            />
                            <FormField
                                label="Account Type"
                                type="select"
                                requiredIcon
                                options={[
                                    { label: 'Saving', value: 'saving' },
                                    { label: 'Current', value: 'current' },
                                ]}
                                error={errors.accountType?.message}
                                {...register('accountType', { required: 'Account Type is required' })}
                            />
                            <FormField
                                label="Account Number"
                                requiredIcon
                                placeholder="e.g. 1234567890"
                                error={errors.accountNumber?.message}
                                {...register('accountNumber', { required: 'Account Number is required' })}
                            />
                        </div>
                    </SectionCard>

                    {/* ── 5. Document & Media Verification ── */}
                    <SectionCard title="Document Verification & Media" icon={<FileText className="w-5 h-5 text-muted-foreground" />}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <Controller
                                name="profilePhoto"
                                control={control}
                                rules={{ required: isEditMode ? false : 'Profile Photo is required' }}
                                render={({ field }) => (
                                    <MultiImageUploader
                                        label="Profile Photo *"
                                        value={field.value || []}
                                        onChange={(val) => handleMediaChange(field, val)}
                                        maxFiles={1}
                                        error={errors.profilePhoto?.message}
                                        type=".jpg,.jpeg,.png,.webp"
                                    />
                                )}
                            />
                            <Controller
                                name="drivingLicense"
                                control={control}
                                rules={{ required: isEditMode ? false : 'Driving License is required' }}
                                render={({ field }) => (
                                    <MultiImageUploader
                                        label="Driving License Scan *"
                                        value={field.value || []}
                                        onChange={(val) => handleMediaChange(field, val)}
                                        maxFiles={1}
                                        error={errors.drivingLicense?.message}
                                        type=".jpg,.jpeg,.png,.webp"
                                    />
                                )}
                            />
                            <Controller
                                name="southAfricaId"
                                control={control}
                                rules={{ required: isEditMode ? false : 'South Africa ID Scan / Document *' }}
                                render={({ field }) => (
                                    <MultiImageUploader
                                        label="South Africa ID *"
                                        value={field.value || []}
                                        onChange={(val) => handleMediaChange(field, val)}
                                        maxFiles={1}
                                        error={errors.southAfricaId?.message}
                                        type=".jpg,.jpeg,.png,.webp"
                                    />
                                )}
                            />
                            <Controller
                                name="vehicleImage"
                                control={control}
                                rules={{ required: isEditMode ? false : 'Vehicle Image is required' }}
                                render={({ field }) => (
                                    <MultiImageUploader
                                        label="Vehicle Photo *"
                                        value={field.value || []}
                                        onChange={(val) => handleMediaChange(field, val)}
                                        maxFiles={1}
                                        error={errors.vehicleImage?.message}
                                        type=".jpg,.jpeg,.png,.webp"
                                    />
                                )}
                            />
                        </div>
                    </SectionCard>

                    {/* ── Submit Buttons ── */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-dashed">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => navigate(-1)}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            leftIcon={<CheckCircle2 className="w-4 h-4" />}
                            isLoading={isSubmitting}
                            loadingText={isEditMode ? "Updating Driver..." : "Registering Driver..."}
                        >
                            {isEditMode ? "Save Changes" : "Create Driver Account"}
                        </Button>
                    </div>
                </form>
            </div>
        </Layout>
    );
};

export default AddDriver;
